import React, { useEffect, useRef } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';

import { LomavisOverlayTemplateCard } from '@/lomavisSharedUi/molecules/cards/LomavisOverlayTemplateCard';
import {
  LomavisTemplateFolderSidebar,
  LomavisTemplateFolderSidebarLabels
} from '@/lomavisSharedUi/organisms/navigation/LomavisTemplateFolderSidebar';
import {
  OverlayTemplateCardLabels,
  OverlayTemplateFolder,
  OverlayTemplateFolderLibrary,
  OverlayTemplateLibrarySelection,
  OverlayTemplateLibraryMode,
  OverlayTemplateSortOption,
  OverlayTemplateSortValue,
  OverlayTemplateSummary
} from '@/lomavisSharedUi/types/overlayTemplates';

export interface LomavisOverlayTemplateLibraryLabels {
  title: string;
  description: string;
  back: string;
  createTemplate: string;
  addFromAccountLibrary: string;
  search: string;
  sort: string;
  emptyTitle: string;
  emptyDescription: string;
  retry: string;
  loading: string;
  templateCount: (count: number) => string;
  folderSidebar: LomavisTemplateFolderSidebarLabels;
  card: OverlayTemplateCardLabels;
}

export interface LomavisOverlayTemplateLibraryProps {
  mode: OverlayTemplateLibraryMode;
  templates: OverlayTemplateSummary[];
  folders: OverlayTemplateFolder[];
  selection?: OverlayTemplateLibrarySelection;
  folderLibrary?: OverlayTemplateFolderLibrary;
  selectedFolderId?: string | null;
  searchValue: string;
  sortValue: OverlayTemplateSortValue;
  sortOptions: OverlayTemplateSortOption[];
  labels: LomavisOverlayTemplateLibraryLabels;
  showPageHeading?: boolean;
  showBackAction?: boolean;
  showCreateAction?: boolean;
  /** Whether to show the All overlays category. Defaults by library mode. */
  showAllCategory?: boolean;
  /** Whether to show the Shared overlays category. Defaults by library mode. */
  showSharedCategory?: boolean;
  /** Whether to show the My overlays category. Defaults by library mode. */
  showMineCategory?: boolean;
  /** @deprecated Account libraries are selected from the sidebar. */
  showAccountLibraryAction?: boolean;
  canManageFolders?: boolean;
  templateCount?: number;
  isLoading?: boolean;
  hasMoreTemplates?: boolean;
  isLoadingMore?: boolean;
  isFolderLoading?: boolean;
  hasMoreFolders?: boolean;
  isFolderLoadingMore?: boolean;
  errorMessage?: string | null;
  loadMoreErrorMessage?: string | null;
  folderErrorMessage?: string | null;
  folderLoadMoreErrorMessage?: string | null;
  onBack?: () => void;
  onCreateTemplate?: () => void;
  /** @deprecated Account libraries are selected from the sidebar. */
  onOpenAccountLibrary?: () => void;
  onSearchChange: (value: string) => void;
  onSortChange: (value: OverlayTemplateSortValue) => void;
  onSelectionChange?: (selection: OverlayTemplateLibrarySelection) => void;
  onFolderSelect?: (folderId: string | null) => void;
  onCreateFolder?: () => void;
  onRenameFolder?: (folder: OverlayTemplateFolder) => void;
  onDeleteFolder?: (folder: OverlayTemplateFolder) => void;
  onEditTemplate?: (templateId: string) => void;
  onChangeTemplateFolder?: (templateId: string) => void;
  onDuplicateTemplate?: (templateId: string) => void;
  onDeleteTemplate?: (templateId: string) => void;
  onStartPost?: (templateId: string) => void;
  onLoadMore?: () => void;
  onLoadMoreFolders?: () => void;
  onRetry?: () => void;
  onRetryLoadMore?: () => void;
  onRetryFolders?: () => void;
  onRetryLoadMoreFolders?: () => void;
}

const TemplateCardSkeleton: React.FC<{ loadingLabel: string }> = ({
  loadingLabel
}) => (
  <div className="card h-100 border bg-white overflow-hidden placeholder-wave">
    <div className="ratio ratio-1x1 bg-light">
      <span className="placeholder w-100 h-100 bg-secondary bg-opacity-10" />
      <span className="visually-hidden">{loadingLabel}</span>
    </div>
    <div className="card-body p-3">
      <span className="placeholder col-8 mb-2 rounded bg-secondary bg-opacity-25" />
      <span className="placeholder col-4 rounded bg-secondary bg-opacity-25" />
    </div>
  </div>
);

/**
 * Template: shared overlay-template library layout for all three product modes.
 */
export const LomavisOverlayTemplateLibrary: React.FC<
  LomavisOverlayTemplateLibraryProps
> = ({
  mode,
  templates,
  folders,
  selection,
  folderLibrary,
  selectedFolderId,
  searchValue,
  sortValue,
  sortOptions,
  labels,
  showPageHeading = true,
  showBackAction = false,
  showCreateAction = false,
  showAllCategory,
  showSharedCategory,
  showMineCategory,
  canManageFolders = false,
  templateCount = templates.length,
  isLoading = false,
  hasMoreTemplates = false,
  isLoadingMore = false,
  isFolderLoading = isLoading,
  hasMoreFolders = false,
  isFolderLoadingMore = false,
  errorMessage,
  loadMoreErrorMessage,
  folderErrorMessage,
  folderLoadMoreErrorMessage,
  onBack,
  onCreateTemplate,
  onSearchChange,
  onSortChange,
  onSelectionChange,
  onFolderSelect,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onEditTemplate,
  onChangeTemplateFolder,
  onDuplicateTemplate,
  onDeleteTemplate,
  onStartPost,
  onLoadMore,
  onLoadMoreFolders,
  onRetry,
  onRetryLoadMore,
  onRetryFolders,
  onRetryLoadMoreFolders
}) => {
  const scrollContentRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = scrollContentRef.current;
    const target = loadMoreSentinelRef.current;
    if (
      !root ||
      !target ||
      !hasMoreTemplates ||
      isLoading ||
      isLoadingMore ||
      loadMoreErrorMessage ||
      !onLoadMore ||
      typeof IntersectionObserver === 'undefined'
    )
      return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { root, rootMargin: '240px 0px', threshold: 0.01 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [
    hasMoreTemplates,
    isLoading,
    isLoadingMore,
    loadMoreErrorMessage,
    onLoadMore,
    templates.length
  ]);

  return (
    <section
      className="container-fluid bg-light py-4 px-3 px-lg-4 h-100 d-flex flex-column gap-3 overflow-hidden"
      data-library-mode={mode}
      aria-label={labels.title}
    >
      {showPageHeading && (
        <header className="flex-shrink-0">
          <h1 className="fs-2 fw-bold text-dark mb-1">{labels.title}</h1>
          <p className="text-muted mb-0">{labels.description}</p>
        </header>
      )}
      <div className="card border shadow-sm bg-white overflow-auto w-100 flex-grow-1">
        <div className="row g-0 align-items-stretch h-100">
          <div className="col-12 col-lg-3 col-xl-2 border-end h-lg-100 overflow-hidden">
            <LomavisTemplateFolderSidebar
              folders={folders}
              selection={selection}
              selectedFolderId={selectedFolderId}
              labels={labels.folderSidebar}
              showAllCategory={showAllCategory ?? mode !== 'account'}
              showSharedCategory={
                showSharedCategory ?? mode !== 'accountPicker'
              }
              showMineCategory={showMineCategory ?? mode === 'profileGroup'}
              folderLibrary={
                folderLibrary ??
                (mode === 'account' || mode === 'accountPicker'
                  ? 'shared'
                  : 'mine')
              }
              canManageFolders={canManageFolders}
              isLoading={isFolderLoading}
              hasMoreFolders={hasMoreFolders}
              isLoadingMoreFolders={isFolderLoadingMore}
              errorMessage={folderErrorMessage}
              loadMoreErrorMessage={folderLoadMoreErrorMessage}
              loadingLabel={labels.loading}
              retryLabel={labels.retry}
              onSelectionChange={onSelectionChange}
              onFolderSelect={onFolderSelect}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onLoadMoreFolders={onLoadMoreFolders}
              onRetryFolders={onRetryFolders}
              onRetryLoadMoreFolders={onRetryLoadMoreFolders}
            />
          </div>

          <main className="col-12 col-lg-9 col-xl-10 bg-white d-flex flex-column overflow-hidden h-lg-100">
            <header className="border-bottom p-3">
              <div className="d-flex flex-column flex-xl-row align-items-xl-center gap-2">
                {showBackAction && onBack && (
                  <Button
                    type="button"
                    size="sm"
                    variant="light"
                    className="btn-icon flex-shrink-0"
                    aria-label={labels.back}
                    onClick={onBack}
                  >
                    <i
                      className="fa-regular fa-arrow-left"
                      aria-hidden="true"
                    />
                  </Button>
                )}

                <div className="position-relative flex-grow-1 w-100">
                  <Form.Control
                    type="search"
                    size="sm"
                    className="ps-9"
                    aria-label={labels.search}
                    placeholder={labels.search}
                    value={searchValue}
                    // Deliberately not disabled while loading. Every keystroke
                    // starts a new query, so disabling here would take the box
                    // away from under the person typing in it — the browser
                    // blurs a disabled field, and the next character goes
                    // nowhere.
                    onChange={(event) => onSearchChange(event.target.value)}
                  />
                  <i
                    className="fa-regular fa-magnifying-glass position-absolute top-50 start-0 translate-middle-y ms-3 text-muted pe-none"
                    aria-hidden="true"
                  />
                </div>

                {!errorMessage && (
                  <span className="text-muted fw-semibold text-nowrap">
                    {labels.templateCount(templateCount)}
                  </span>
                )}

                <Form.Select
                  size="sm"
                  className="w-auto flex-shrink-0"
                  aria-label={labels.sort}
                  value={sortValue}
                  disabled={isLoading}
                  onChange={(event) =>
                    onSortChange(event.target.value as OverlayTemplateSortValue)
                  }
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>

                <div className="d-flex flex-wrap flex-sm-nowrap gap-2">
                  {showCreateAction && onCreateTemplate && (
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      className="text-nowrap"
                      onClick={onCreateTemplate}
                    >
                      <i
                        className="fa-regular fa-plus me-2"
                        aria-hidden="true"
                      />
                      {labels.createTemplate}
                    </Button>
                  )}
                </div>
              </div>
              {!showPageHeading && (
                <p className="visually-hidden">{labels.description}</p>
              )}
            </header>

            <div
              ref={scrollContentRef}
              className="p-4 flex-grow-1 overflow-auto"
            >
              {errorMessage && (
                <Alert
                  variant="danger"
                  className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3"
                >
                  <span>{errorMessage}</span>
                  {onRetry && (
                    <Button
                      type="button"
                      size="sm"
                      variant="danger"
                      onClick={onRetry}
                    >
                      {labels.retry}
                    </Button>
                  )}
                </Alert>
              )}

              {!errorMessage && (
                <>
                  {isLoading && (
                    <div className="row g-3" aria-label={labels.loading}>
                      {[0, 1, 2, 3, 4, 5].map((position) => (
                        <div
                          className="col-12 col-sm-6 col-lg-4 col-xxl-3"
                          key={position}
                        >
                          <TemplateCardSkeleton loadingLabel={labels.loading} />
                        </div>
                      ))}
                    </div>
                  )}

                  {!isLoading && templates.length === 0 && (
                    <div className="d-flex h-100 flex-column align-items-center justify-content-center text-center px-4">
                      <span className="symbol symbol-70px bg-light-primary rounded-circle mb-4 d-inline-flex align-items-center justify-content-center">
                        <i
                          className="fa-regular fa-layer-group fs-1 text-primary"
                          aria-hidden="true"
                        />
                      </span>
                      <h2 className="fs-4 fw-bold text-dark mb-2">
                        {labels.emptyTitle}
                      </h2>
                      <p className="text-muted mb-4">
                        {labels.emptyDescription}
                      </p>
                      {showCreateAction && onCreateTemplate && (
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          onClick={onCreateTemplate}
                        >
                          {labels.createTemplate}
                        </Button>
                      )}
                    </div>
                  )}

                  {!isLoading && templates.length > 0 && (
                    <>
                      <div className="row g-3">
                        {templates.map((template) => {
                          const isPicker = mode === 'accountPicker';
                          const canManageTemplate =
                            mode === 'account' ||
                            (mode === 'profileGroup' && !template.isShared);

                          return (
                            <div
                              className="col-12 col-sm-6 col-lg-4 col-xxl-3"
                              key={template.id}
                            >
                              <LomavisOverlayTemplateCard
                                template={template}
                                labels={labels.card}
                                showAspectRatioOnPreview={isPicker}
                                onEdit={
                                  canManageTemplate ? onEditTemplate : undefined
                                }
                                onChangeFolder={
                                  canManageTemplate
                                    ? onChangeTemplateFolder
                                    : undefined
                                }
                                // Copying is the one action that does not
                                // depend on owning the overlay: a duplicate is
                                // a new overlay in your own library, so it is
                                // offered for shared ones and in the picker
                                // too.
                                onDuplicate={onDuplicateTemplate}
                                onDelete={
                                  canManageTemplate
                                    ? onDeleteTemplate
                                    : undefined
                                }
                                onStartPost={
                                  mode === 'account' ? undefined : onStartPost
                                }
                              />
                            </div>
                          );
                        })}
                      </div>

                      {isLoadingMore && (
                        <div
                          className="d-flex justify-content-center align-items-center gap-2 py-4 text-muted"
                          role="status"
                        >
                          <span
                            className="spinner-border spinner-border-sm"
                            aria-hidden="true"
                          />
                          <span>{labels.loading}</span>
                        </div>
                      )}

                      {loadMoreErrorMessage && (
                        <div className="d-flex flex-column flex-sm-row justify-content-center align-items-sm-center gap-2 py-4">
                          <span className="text-danger">
                            {loadMoreErrorMessage}
                          </span>
                          {onRetryLoadMore && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-danger"
                              onClick={onRetryLoadMore}
                            >
                              {labels.retry}
                            </Button>
                          )}
                        </div>
                      )}

                      <div
                        ref={loadMoreSentinelRef}
                        className="w-100"
                        style={{ height: 1 }}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>
    </section>
  );
};
